import { IndicatorsDocument } from 'lib/api/collections/types';
import TableCard from 'lib/designSystem/Card/TableCard';
import BarChart from 'lib/designSystem/Charts/BarChart';
import EnhancedTable, { Header } from 'lib/designSystem/Table';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React, { useState } from 'react';
import { ResponsiveContainer } from 'recharts';

import { CustomToggleWrapper } from '../../index';

const countTableHeaders = [
  {
    key: 'name',
    label: 'Ejecutivo',
  },
  {
    key: 'totalCount',
    label: 'Total',
  },
  {
    key: 'onDateDocumentsCount',
    label: 'Vigente',
  },
  {
    key: 'dued1to30DaysDocumentsCount',
    label: 'Vencido 1 a 30 días',
  },
  {
    key: 'dued31to60DaysDocumentsCount',
    label: 'Vencido 31 a 60 días',
  },
  {
    key: 'dued61to90DaysDocumentsCount',
    label: 'Vencido 61 a 90 días',
  },
  {
    key: 'duedMore90DaysDocumentsCount',
    label: 'Vencido más de 90 días',
  },
];

const amountTableHeaders = [
  {
    key: 'name',
    label: 'Ejecutivo',
    type: 'money',
  },
  {
    key: 'totalAmount',
    label: 'Total',
    type: 'money',
  },
  {
    key: 'onDateDocumentsAmount',
    label: 'Vigente',
    type: 'money',
  },
  {
    key: 'dued1to30DaysDocumentsAmount',
    label: 'Vencido 1 a 30 días',
    type: 'money',
  },
  {
    key: 'dued31to60DaysDocumentsAmount',
    label: 'Vencido 31 a 60 días',
    type: 'money',
  },
  {
    key: 'dued61to90DaysDocumentsAmount',
    label: 'Vencido 61 a 90 días',
    type: 'money',
  },
  {
    key: 'duedMore90DaysDocumentsAmount',
    label: 'Vencido más de 90 días',
    type: 'money',
  },
];

type ByExecutiveChartsProps = {
  documentsV2Data: IndicatorsDocument[];
};

const ByExecutiveCharts = ({ documentsV2Data }: ByExecutiveChartsProps) => {
  const [show, setShow] = useState<'bars' | 'table'>('bars');
  const [headersType, setHeadersType] = useState<'Amount' | 'Count'>('Amount');
  const [barsType, setBarsType] = useState<'Amount' | 'Count'>('Amount');
  const [tableHeaders, setTableHeaders] =
    useState<Header[]>(amountTableHeaders);

  const tableDataByCollector = documentsV2Data.reduce(
    (
      acc: Record<string, IndicatorsDocument[]>,
      document: IndicatorsDocument,
    ) => {
      if (!acc[document.collectorName]) {
        acc[document.collectorName] = [document];
      } else {
        acc[document.collectorName].push(document);
      }
      return acc;
    },
    {},
  );

  const tableDataSource = Object.entries(tableDataByCollector).map(
    ([collectorName, documents]: [string, IndicatorsDocument[]]) => {
      const uniqueStakeholderCount = new Set(
        documents
          .map((d) => d.stakeholderIdentifier)
          .filter((id): id is string => id != null && id !== ''),
      ).size;
      const onDateDocuments = documents.filter(
        (document) => document.arrearTrench === 'Vigente',
      );
      const dued1to30DaysDocuments = documents.filter(
        (document) => document.arrearTrench === '1 a 30 días',
      );
      const dued31to60DaysDocuments = documents.filter(
        (document) => document.arrearTrench === '31 a 60 días',
      );
      const dued61to90DaysDocuments = documents.filter(
        (document) => document.arrearTrench === '61 a 90 días',
      );
      const duedMore90DaysDocuments = documents.filter(
        (document) => document.arrearTrench === 'Más de 90 días',
      );
      return {
        name: `${collectorName} (${uniqueStakeholderCount} deudores)`,
        onDateDocumentsCount: onDateDocuments.length,
        onDateDocumentsAmount: onDateDocuments.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
        dued1to30DaysDocumentsCount: dued1to30DaysDocuments.length,
        dued1to30DaysDocumentsAmount: dued1to30DaysDocuments.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
        dued31to60DaysDocumentsCount: dued31to60DaysDocuments.length,
        dued31to60DaysDocumentsAmount: dued31to60DaysDocuments.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
        dued61to90DaysDocumentsCount: dued61to90DaysDocuments.length,
        dued61to90DaysDocumentsAmount: dued61to90DaysDocuments.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
        duedMore90DaysDocumentsCount: duedMore90DaysDocuments.length,
        duedMore90DaysDocumentsAmount: duedMore90DaysDocuments.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
        totalCount: documents.length,
        totalAmount: documents.reduce(
          (acc, document) => acc + document.amount,
          0,
        ),
      };
    },
  );

  const tableDataAmount = [...tableDataSource]
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .map((data) => ({
      name: data.name,
      Vigente: data.onDateDocumentsAmount,
      ['1 a 30 días']: data.dued1to30DaysDocumentsAmount,
      ['31 a 60 días']: data.dued31to60DaysDocumentsAmount,
      ['61 a 90 días']: data.dued61to90DaysDocumentsAmount,
      ['Más de 90 días']: data.duedMore90DaysDocumentsAmount,
    }));

  const tableDataCount = [...tableDataSource]
    .sort((a, b) => b.totalCount - a.totalCount)
    .map((data) => ({
      name: data.name,
      Vigente: data.onDateDocumentsCount,
      ['1 a 30 días']: data.dued1to30DaysDocumentsCount,
      ['31 a 60 días']: data.dued31to60DaysDocumentsCount,
      ['61 a 90 días']: data.dued61to90DaysDocumentsCount,
      ['Más de 90 días']: data.duedMore90DaysDocumentsCount,
    }));

  const changeTableHeaders = () => {
    if (headersType === 'Count') {
      setHeadersType('Amount');
      setTableHeaders(amountTableHeaders);
    } else {
      setHeadersType('Count');
      setTableHeaders(countTableHeaders);
    }
  };

  const header = () => {
    return (
      <div
        style={{
          flexDirection: 'row',
          display: 'flex',
          alignItems: 'center',
          width: '90%',
          justifyContent: 'center',
          alignSelf: 'center',
          paddingLeft: '5%',
        }}
      >
        <CustomToggleWrapper
          value={show}
          onChange={(_: string) => {
            setShow(show === 'bars' ? 'table' : 'bars');
            setTableHeaders(amountTableHeaders);
            setHeadersType('Amount');
            setBarsType('Amount');
          }}
          options={[
            { value: 'bars', label: 'Grafico' },
            { value: 'table', label: 'Tabla' },
          ]}
        />
        {show === 'bars' && (
          <div style={{ marginLeft: 'auto' }}>
            <CustomToggleWrapper
              value={barsType}
              onChange={(v) => {
                setBarsType(v as 'Amount' | 'Count');
              }}
              options={[
                { value: 'Amount', label: 'Monto' },
                { value: 'Count', label: 'Documentos' },
              ]}
            />
          </div>
        )}
        {show === 'table' && (
          <div style={{ marginLeft: 'auto' }}>
            <CustomToggleWrapper
              value={headersType}
              onChange={(_: string) => {
                changeTableHeaders();
              }}
              options={[
                { value: 'Amount', label: 'Monto' },
                { value: 'Count', label: 'Documentos' },
              ]}
            />
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ marginTop: '16px' }}>
      <TableCard>
        {header()}
        {show === 'bars' && (
          <div>
            <ResponsiveContainer
              width="98%"
              height={window.innerHeight * 0.24}
              style={{ padding: '5px' }}
            >
              <BarChart
                data={barsType === 'Amount' ? tableDataAmount : tableDataCount}
                colors={[
                  CHART_COLORS.green,
                  CHART_COLORS.amber,
                  CHART_COLORS.orange,
                  CHART_COLORS.red,
                  CHART_COLORS.burgundy,
                ]}
                stacked={true}
                categoryGapRatio={0.4}
                direction="horizontal"
              />
            </ResponsiveContainer>
          </div>
        )}
        {show === 'table' && (
          <div style={{ padding: '5px' }}>
            <EnhancedTable
              data={tableDataSource}
              dataHeaders={tableHeaders}
              finalSumRow={headersType === 'Amount'}
              exportToFile="xlsx"
            />
          </div>
        )}
      </TableCard>
    </div>
  );
};

export default ByExecutiveCharts;
